import React, { useState, useEffect } from "react";
import {
  Card,
  Collapse,
  Tag,
  Row,
  Col,
  Button,
  Spin,
  Alert,
  Empty,
} from "antd";
import {
  CheckOutlined,
  StarOutlined,
  LoadingOutlined,
  ExperimentOutlined,
} from "@ant-design/icons";
import StepHeader from "./StepsHeader";

const { Panel } = Collapse;

export default function SelectPackageStep({
  onBack,
  onNext,
  packages = [],
  loading = false,
  error = null,
  selectedPackageId,
}) {
  const [selectedPackage, setSelectedPackage] = useState(
    selectedPackageId || null
  );

  useEffect(() => {
    if (!selectedPackage && packages?.length > 0) {
      const defaultPkg = packages.find((pkg) => pkg.isDefaultPackage);
      if (defaultPkg) {
        setSelectedPackage(defaultPkg._id);
      }
    }
  }, [packages, selectedPackage]);

  const handleNext = () => {
    const selectedPkg = packages.find((pkg) => pkg._id === selectedPackage);
    if (selectedPkg) {
      onNext(selectedPkg);
    }
  };

  const handleBack = () => {
    const selectedPkg = packages.find((pkg) => pkg._id === selectedPackage);
    onBack(selectedPkg);
  };

  if (loading) {
    return (
      <div>
        <StepHeader current={1} />
        <div style={{ textAlign: "center", padding: "100px 0" }}>
          <Spin size="large" indicator={<LoadingOutlined spin />} />
          <p style={{ marginTop: 20, color: "#666" }}>
            Loading available packages...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <StepHeader current={1} />
        <Alert
          message="Error Loading Packages"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 20 }}
        />
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Button onClick={onBack}>Back</Button>
          <Button type="primary" disabled>
            Next →
          </Button>
        </div>
      </div>
    );
  }

  if (!packages || packages.length === 0) {
    return (
      <div>
        <StepHeader current={1} />
        <Empty
          description="No packages available for the selected commodity and variant"
          style={{ margin: "100px 0" }}
        />
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Button onClick={onBack}>Back</Button>
          <Button type="primary" disabled>
            Next →
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <StepHeader current={1} />

      <h3>Available Packages</h3>
      <p style={{ color: "#666", marginBottom: 20 }}>
        Select a testing package for your sample
      </p>

      {packages.map((pkg) => {
        const isSelected = selectedPackage === pkg._id;
        const categoryEntries = Object.entries(pkg.testCategoryCounts || {});

        return (
          <Card
            key={pkg._id}
            style={{
              marginBottom: 18,
              border: isSelected ? "2px solid #000" : "1px solid #ddd",
              borderRadius: 10,
            }}
            bodyStyle={{ padding: 20 }}
            onClick={() => setSelectedPackage(pkg._id)}
            hoverable
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: 0 }}>{pkg.name}</h2>
                <p style={{ margin: "4px 0", color: "#666" }}>
                  {pkg.commodity?.name || "Quality testing package"}
                </p>

                <div style={{ marginTop: 10 }}>
                  <Tag color="blue">
                    {pkg.totalParametersForTest || 0} Total Tests
                  </Tag>
                  <Tag color="geekblue">
                    {pkg.categoriesCount || categoryEntries.length} Categories
                  </Tag>
                  {pkg.sampleVariant && (
                    <Tag color="purple">{pkg.sampleVariant}</Tag>
                  )}
                </div>
              </div>

              <div style={{ display: "flex", gap: 12 }}>
                {pkg.isDefaultPackage && (
                  <div>
                    <Tag color="#001529" style={{ margin: 0 }}>
                      <StarOutlined /> Default
                    </Tag>
                  </div>
                )}
                <div
                  style={{
                    height: 28,
                    width: 28,
                    borderRadius: "50%",
                    border: isSelected ? "2px solid #000" : "2px solid #d9d9d9",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    background: isSelected ? "#000" : "transparent",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  {isSelected && <CheckOutlined style={{ color: "#fff" }} />}
                </div>
              </div>
            </div>

            {categoryEntries.length > 0 && (
              <Collapse bordered={false} ghost style={{ marginTop: 15 }}>
                <Panel
                  header={
                    <div style={{ fontWeight: 500 }}>
                      View Test Categories & Parameters
                    </div>
                  }
                  key="categories"
                >
                  <Collapse
                    bordered={false}
                    style={{ background: "#fafafa" }}
                    expandIconPosition="end"
                  >
                    {categoryEntries.map(([categoryName, categoryData]) => {
                      const paramCount = categoryData.count || 0;
                      const parameters = categoryData.parameters || [];

                      return (
                        <Panel
                          key={categoryName}
                          header={
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                paddingRight: 10,
                              }}
                            >
                              <span style={{ fontWeight: 500 }}>
                                <ExperimentOutlined
                                  style={{ marginRight: 8 }}
                                />
                                {categoryName}
                              </span>
                              <Tag color="blue">
                                {paramCount}{" "}
                                {paramCount === 1 ? "test" : "tests"}
                              </Tag>
                            </div>
                          }
                          style={{
                            marginBottom: 8,
                            background: "white",
                            borderRadius: 6,
                            border: "1px solid #e8e8e8",
                          }}
                        >
                          {parameters.length > 0 ? (
                            <Row gutter={[10, 10]}>
                              {parameters.map((parameter, index) => (
                                <Col
                                  span={12}
                                  key={`${categoryName}-${parameter}-${index}`}
                                >
                                  <div
                                    style={{
                                      background: "#f5f7fb",
                                      padding: "10px 14px",
                                      borderRadius: 6,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 8,
                                      border: "1px solid #e3e8f0",
                                    }}
                                  >
                                    <CheckOutlined
                                      style={{ color: "#0047ff", fontSize: 12 }}
                                    />
                                    <span style={{ fontSize: 13 }}>
                                      {parameter}
                                    </span>
                                  </div>
                                </Col>
                              ))}
                            </Row>
                          ) : (
                            <div
                              style={{
                                textAlign: "center",
                                color: "#999",
                                padding: "20px",
                                fontStyle: "italic",
                              }}
                            >
                              No parameters available for this category
                            </div>
                          )}
                        </Panel>
                      );
                    })}
                  </Collapse>
                </Panel>
              </Collapse>
            )}

            <div
              style={{
                marginTop: 15,
                paddingTop: 15,
                borderTop: "1px solid #f0f0f0",
              }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <small style={{ color: "#666" }}>
                    <strong>Company:</strong>{" "}
                    {pkg.company?.companyName || "N/A"}
                  </small>
                </Col>
                <Col span={12}>
                  <small style={{ color: "#666" }}>
                    <strong>Commodity:</strong> {pkg.commodity?.name || "N/A"}
                  </small>
                </Col>
              </Row>
            </div>
          </Card>
        );
      })}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 30,
        }}
      >
        <Button onClick={handleBack}>Back</Button>
        <Button type="primary" onClick={handleNext} disabled={!selectedPackage}>
          Next →
        </Button>
      </div>
    </div>
  );
}
